import React, { useEffect, useRef, useState } from 'react';
import { PermissionAwareInput } from '../../../contexts/PermissionAccessContext';
import safetySystemService from '../../../services/safetySystem.service';
import type { UnitRow, ViolationLookupRow } from '../../../types/safetySystem';
import { inputClass } from './unitFields';

/**
 * Prefix typeaheads for the Ticket drawer. Both debounce the server call, cancel stale
 * requests, and render their hits as a listbox under the input. They are PermissionAware
 * inputs, so they disable themselves below edit level inside the tab's provider.
 */

const DEBOUNCE_MS = 200;

interface TypeaheadProps<T> {
  ariaLabel: string;
  value: string;
  onChange: (text: string) => void;
  onPick: (hit: T) => void;
  fetchHits: (q: string, signal: AbortSignal) => Promise<T[]>;
  renderHit: (hit: T) => string;
  hitKey: (hit: T) => string;
  placeholder?: string;
  className?: string;
}

function Typeahead<T>({ ariaLabel, value, onChange, onPick, fetchHits, renderHit, hitKey, placeholder, className }: TypeaheadProps<T>) {
  const [hits, setHits] = useState<T[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (query === null) return;
    const q = query.trim();
    if (!q) { setHits([]); setOpen(false); return; }
    const controller = new AbortController();
    timer.current = setTimeout(() => {
      fetchHits(q, controller.signal)
        .then((rows) => { if (!controller.signal.aborted) { setHits(rows); setOpen(true); } })
        .catch(() => { if (!controller.signal.aborted) setHits([]); });
    }, DEBOUNCE_MS);
    return () => {
      controller.abort();
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query, fetchHits]);

  const pick = (hit: T) => {
    setOpen(false);
    setHits([]);
    setQuery(null);
    onPick(hit);
  };

  return (
    <div className={`relative ${className ?? ''}`}>
      <PermissionAwareInput
        type="text"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={open}
        autoComplete="off"
        className={inputClass}
        value={value}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setQuery(e.target.value); }}
        onBlur={() => { setTimeout(() => setOpen(false), 150); }}
        onFocus={() => { if (hits.length) setOpen(true); }}
      />
      {open && hits.length > 0 && (
        <ul role="listbox" aria-label={`${ariaLabel} suggestions`}
          className="absolute left-0 z-20 mt-0.5 max-h-56 w-full min-w-[16rem] overflow-y-auto rounded-md border border-rule bg-white py-1 text-sm shadow-lg dark:bg-gray-900">
          {hits.map((hit) => (
            <li key={hitKey(hit)} role="option" aria-selected={false} tabIndex={-1}
              onMouseDown={(e) => { e.preventDefault(); pick(hit); }}
              onClick={() => pick(hit)}
              className="cursor-pointer px-2 py-1 hover:bg-highlight">
              {renderHit(hit)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const fetchViolations = async (q: string, signal: AbortSignal): Promise<ViolationLookupRow[]> =>
  (await safetySystemService.violationLookup(q, signal)).data ?? [];

/** Violation code typeahead over GET /tickets/lookups/violations. */
export const ViolationTypeahead: React.FC<{
  ariaLabel: string; value: string; onChange: (text: string) => void; onPick: (hit: ViolationLookupRow) => void; className?: string;
}> = (props) => (
  <Typeahead<ViolationLookupRow>
    {...props}
    fetchHits={fetchViolations}
    hitKey={(h) => h.violation}
    renderHit={(h) => `${h.violation} — ${h.violationdesc ?? ''}${h.csabasics ? ` (${h.csabasics})` : ''}`}
    placeholder="Code…"
  />
);

const fetchDrivers = async (q: string, signal: AbortSignal): Promise<UnitRow[]> =>
  ((await safetySystemService.listUnits({ view: 'driver', status: 'ALL', q, search_by: 'drivername' }, signal)).data ?? [])
    .filter((r) => r.driverrecno);

export const driverLabel = (r: { unitnumber?: unknown; drivername?: unknown }) => `${r.unitnumber ?? ''} - ${r.drivername ?? ''}`.trim();

/** Driver picker over the Units list (driver view, every status, name prefix search). */
export const DriverTypeahead: React.FC<{
  ariaLabel: string; value: string; onChange: (text: string) => void; onPick: (hit: UnitRow) => void; className?: string;
}> = (props) => (
  <Typeahead<UnitRow>
    {...props}
    fetchHits={fetchDrivers}
    hitKey={(h) => String(h.driverrecno)}
    renderHit={driverLabel}
    placeholder="Type a driver name…"
  />
);

const fetchUnits = async (q: string, signal: AbortSignal): Promise<UnitRow[]> =>
  ((await safetySystemService.listUnits({ view: 'truck', status: 'ALL', q, search_by: 'unitnumber' }, signal)).data ?? [])
    .filter((r) => r.unitnumber);

export const unitLabel = (r: { unitnumber?: unknown; drivername?: unknown; truckvin?: unknown }) =>
  `${r.unitnumber ?? ''}${r.drivername ? ` - ${r.drivername}` : ''}${r.truckvin ? ` (${r.truckvin})` : ''}`.trim();

/** Unit picker over the Units list (truck view, every status, exact unit number) — fills truck / owner on an incident equipment row. */
export const UnitTypeahead: React.FC<{
  ariaLabel: string; value: string; onChange: (text: string) => void; onPick: (hit: UnitRow) => void; className?: string;
}> = (props) => (
  <Typeahead<UnitRow>
    {...props}
    fetchHits={fetchUnits}
    hitKey={(h) => `${h.unitrecno ?? ''}-${h.truckrecno ?? ''}`}
    renderHit={unitLabel}
    placeholder="Unit #…"
  />
);
